import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyContext = createContext({ company: null, loading: true, refresh: () => {}, displayMode: "native" });

/**
 * CompanyProvider fetches the active company (resolved by backend X-Company-Id header)
 * and exposes it to all children.
 *
 * The display currency mode is set TENANT-WIDE by the admin via /admin Settings
 * (`company.display_currency`: "native" | "eur"). Defaults to "native".
 * No per-user toggle is needed.
 */
export const CompanyProvider = ({ children }) => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) { setCompany(null); setLoading(false); return; }
    try {
      // Use /auth/me to get current_user.company_id then fetch that company
      const headers = { Authorization: `Bearer ${token}` };
      const active = localStorage.getItem("active_company_id");
      if (active) headers["X-Company-Id"] = active;
      const me = await axios.get(`${API}/auth/me`, { headers });
      const cid = me.data?.company_id || "giotto-italia";
      const r = await axios.get(`${API}/companies/${cid}`, { headers });
      setCompany(r.data);
    } catch {
      setCompany(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh when superadmin switches tenant (active_company_id in localStorage changes)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "active_company_id") refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refresh]);

  // Derive displayMode from company config (set by admin in /admin Settings)
  const displayMode = (company?.display_currency === "eur" && company?.currency !== "EUR") ? "eur" : "native";

  return (
    <CompanyContext.Provider value={{ company, loading, refresh, displayMode }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => useContext(CompanyContext);
